/*
  Hustle popup frequency controller.

  Works with the server-side gate in Chisel\HustlePopup. Reads settings from
  window.x5script.popup (localized in Chisel\Extensions\Theme::localize_front_end_scripts):

    moduleId     : managed Hustle module id
    formId       : Gravity Form id embedded in the popup
    sequenceDays : array of day intervals, e.g. [2, 3, 7, 14, 30]
    cookies      : { subscribed, count, nextDue }

  Behaviour:
    - Each time the popup is shown, schedule the next allowed show further out
      using the configured day sequence (last value repeats), so a visitor sees
      it once on entering the site and not again on returning to the page.
    - A successful Gravity Form submission inside the popup suppresses it forever.
    - Re-check cookies before every automatic show so a cached page (where the
      server-side gate never runs) can't bypass the gate.
*/

const DAY = 86400;
const DEFAULT_SEQUENCE = [2, 3, 7, 14, 30];

function getCookie(name) {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const re = new RegExp(`(?:^|; )${escaped}=([^;]*)`);
  const match = document.cookie.match(re);

  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * DAY * 1000).toUTCString();
  const encoded = encodeURIComponent(value);

  document.cookie = `${name}=${encoded}; expires=${expires}; path=/; SameSite=Lax`;
}

class PopupFrequency {
  constructor() {
    this.cfg = (window.x5script && window.x5script.popup) || {};

    if (!this.cfg.moduleId || typeof window.jQuery === 'undefined') {
      return;
    }

    this.sequence =
      Array.isArray(this.cfg.sequenceDays) && this.cfg.sequenceDays.length
        ? this.cfg.sequenceDays.map(Number)
        : DEFAULT_SEQUENCE;

    this.cookies = this.cfg.cookies || {
      subscribed: 'x5_popup_subscribed',
      count: 'x5_popup_close_count',
      nextDue: 'x5_popup_next_due',
    };

    this.init();
  }

  isOurModule(view) {
    if (!view) {
      return true; // single managed popup: treat any module as ours
    }

    const id = parseInt(view.moduleId || (view.$el && view.$el.data('id')), 10);

    return !id || id === parseInt(this.cfg.moduleId, 10);
  }

  isSuppressed() {
    if (getCookie(this.cookies.subscribed)) {
      return true;
    }

    const nextDue = parseInt(getCookie(this.cookies.nextDue), 10) || 0;

    return Math.floor(Date.now() / 1000) < nextDue;
  }

  scheduleNext() {
    const count = (parseInt(getCookie(this.cookies.count), 10) || 0) + 1;
    const step = Math.min(count - 1, this.sequence.length - 1);
    const nextDue = Math.floor(Date.now() / 1000) + this.sequence[step] * DAY;

    setCookie(this.cookies.count, count, 400);
    setCookie(this.cookies.nextDue, nextDue, 400);
  }

  init() {
    const $ = window.jQuery;

    // Capture-phase so this runs before Hustle's own delegated click handler,
    // letting an explicit click on the nav email link bypass the cooldown set
    // by an earlier automatic (time/scroll) show.
    document.addEventListener(
      'click',
      e => {
        if (!e.target.closest('.email-signup')) {
          return;
        }

        // Hustle's own click trigger calls display(), which is blocked by its
        // shouldDisplay() gate once the "never see"/after-close cookies are set
        // (e.g. the visitor clicked "never show this again"). Open the module
        // directly so the nav link always works regardless of that gate.
        const module =
          window.Hustle &&
          typeof window.Hustle.getModule === 'function' &&
          window.Hustle.getModule(this.cfg.moduleId);

        // Decide on the popup's actual DOM visibility (the .hustle-show class),
        // not module.isShown: some close paths (e.g. the auto close-delay) leave
        // isShown stale-true while the popup is hidden, which would otherwise
        // block reopening it via the nav link.
        if (module && typeof module.displayModule === 'function') {
          const isVisible = !!(
            module.$el && module.$el.hasClass('hustle-show')
          );

          if (!isVisible) {
            e.preventDefault();
            module.isShown = false; // clear any stale flag before reopening
            module.displayModule();
          }
        }
      },
      true,
    );

    // Gate automatic shows (time/scroll/exit triggers) at the source: they all
    // go through module.display(), which bails out when shouldDisplay() is
    // false. Hustle's before_show event can't be used for this, as it fires
    // right before the popup is shown with no way to cancel it (and
    // onModuleClosed() only resets state, it doesn't hide the popup). The nav
    // link opens the module via displayModule(), which skips this check.
    const gateModule = () => {
      const module =
        window.Hustle &&
        typeof window.Hustle.getModule === 'function' &&
        window.Hustle.getModule(this.cfg.moduleId);

      if (!module || typeof module.shouldDisplay !== 'function') {
        return false;
      }

      const shouldDisplay = module.shouldDisplay.bind(module);

      module.shouldDisplay = () => !this.isSuppressed() && shouldDisplay();

      return true;
    };

    // Hustle builds its modules when its script loads, normally before ours.
    if (!gateModule()) {
      $(document).one('hustleInitialized', gateModule);
    }

    // Every show, automatic or from the nav link, counts as the visitor having
    // seen it: push the next automatic show out.
    $(document).on('hustle:module:before_show', (e, view) => {
      if (!this.isOurModule(view)) {
        return;
      }

      this.scheduleNext();
    });

    // Permanent suppression once the embedded Gravity Form is submitted.
    $(document).on('gform_confirmation_loaded', (e, formId) => {
      const ourForm =
        !this.cfg.formId ||
        parseInt(formId, 10) === parseInt(this.cfg.formId, 10);

      if (ourForm) {
        setCookie(this.cookies.subscribed, '1', 3650);
      }
    });
  }
}

export default new PopupFrequency();



// WEBPACK FOOTER //
// ./src/scripts/modules/popup.js